import { useState } from 'react';

import { Badge } from '@frontend/components/ui/badge';
import { Button } from '@frontend/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@frontend/components/ui/dialog';
import { Input } from '@frontend/components/ui/input';
import { Label } from '@frontend/components/ui/label';
import { useUsers } from '@frontend/features/admin/hooks/useUsers';
import type { Folder } from '@frontend/features/documents/types';
import { Loader2, UserPlus, X } from 'lucide-react';

interface ShareFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder: Folder;
  onSave: (sharedWith: string[]) => void;
  isSaving: boolean;
}

export function ShareFolderDialog({
  open,
  onOpenChange,
  folder,
  onSave,
  isSaving,
}: ShareFolderDialogProps) {
  const [sharedWith, setSharedWith] = useState<string[]>(
    folder.sharedWith ?? [],
  );
  const [inputValue, setInputValue] = useState('');
  const { users } = useUsers();

  // Filter users that aren't already shared with and aren't the owner
  const availableUsers = users.filter(
    (u) => !sharedWith.includes(u.email) && u.email !== folder.createdBy,
  );

  const matchingUsers = inputValue.trim()
    ? availableUsers.filter((u) =>
        u.email.toLowerCase().includes(inputValue.toLowerCase()),
      )
    : [];

  const handleAddUser = (email: string) => {
    if (!sharedWith.includes(email)) {
      setSharedWith([...sharedWith, email]);
    }
    setInputValue('');
  };

  const handleRemoveUser = (email: string) => {
    setSharedWith(sharedWith.filter((e) => e !== email));
  };

  const handleSave = () => {
    onSave(sharedWith);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSharedWith(folder.sharedWith ?? []);
      setInputValue('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>Share &quot;{folder.name}&quot;</DialogTitle>
          <DialogDescription>
            Add users who can view and manage documents in this folder.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Add user</Label>
            <div className="relative">
              <div className="flex gap-2">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Search by email..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && matchingUsers.length === 1) {
                      e.preventDefault();
                      handleAddUser(matchingUsers[0]!.email);
                    }
                  }}
                />
              </div>
              {matchingUsers.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-border bg-popover shadow-md">
                  {matchingUsers.slice(0, 5).map((user) => (
                    <button
                      key={user.email}
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-[13px] hover:bg-muted transition-colors first:rounded-t-lg last:rounded-b-lg"
                      onClick={() => handleAddUser(user.email)}
                    >
                      <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
                      {user.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Shared with</Label>
            {sharedWith.length === 0 ? (
              <p className="text-[13px] text-muted-foreground py-3 text-center">
                Not shared with anyone
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {sharedWith.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="gap-1.5 pr-1 text-[12px]"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveUser(email)}
                      className="rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
